import { useCallback } from 'react';
import {
  updateCorrelationColorConfigData,
  updateCorrelationFontSizeConfigData,
  updateCorrelationChartColorScheme,
} from 'src/stores/actions/charts';
import { dataToColors, getChartThemeColors } from 'src/util/colors';
import { CORRELATION_CARD } from 'src/sections/GroupCards/cardTypes';
import ThemeManager from '../ThemeManager';

const REDUX_CONFIG_KEY = 'correlationChartConfigData';

/**
 * Gerenciador de tema do card de correlação.
 *
 * Reusa toda a lógica do `ThemeManager`:
 *   - `useSeries` (com strategies por chartType) extrai a lista de perguntas
 *     correlacionadas únicas em ordem canônica.
 *   - `calculateChartColorsArray` calcula o array `colors` ordenado por essa lista.
 *   - `_setChartColorsOnInit` dispara o recálculo na inicialização e a cada mudança
 *     de payload, tema ou série.
 *
 * Overrides:
 *   - `_updateChartColors`, `updateFontSize` e `updateColorScheme` carregam a flag
 *     `isAuto` para que a action de persistência escolha o chartType correto na URL
 *     (`correlationAutoChart` vs `correlationManualChart`).
 *
 * O `analyzeId` é, por convenção:
 *   - auto:   `questionId` da pergunta principal selecionada
 *   - manual: `configurationId` da configuração de correlação selecionada
 */
export default class CorrelationChartThemeManager extends ThemeManager {
  constructor(analyzeId, surveyId, withData, chartRef, appTheme, isAuto, chartType) {
    super(analyzeId, surveyId, withData, chartRef, appTheme, chartType, CORRELATION_CARD);

    this._isAuto = isAuto;

    // Bindings
    this._recalculateColorArray = this._recalculateColorArray.bind(this);
    this._setChartConfiguration = this._setChartConfiguration.bind(this);
    this._setChartSeries = this._setChartSeries.bind(this);
    this._setActions = this._setActions.bind(this);
    this._setChartColorsOnInit = this._setChartColorsOnInit.bind(this);
    this._setChartInitializationState = this._setChartInitializationState.bind(this);
    this.getConfig = this.getConfig.bind(this);

    // Pipeline padrão de inicialização — equivalente ao SimpleChartThemeManager
    this._setChartSeries();
    this._setChartConfiguration(REDUX_CONFIG_KEY);
    this._setActions();
    this._setChartInitializationState();
    this._setChartColorsOnInit();
  }

  /**
   * Override: a action de persistência de correlação carrega `isAuto` para escolher
   * entre o chartType `correlationAutoChart` ou `correlationManualChart`.
   */
  _updateChartColors() {
    return useCallback(
      (enunciadoAlterado, newColor) => {
        const chartConfigDataPayload = {
          ...this._payload,
          chartsColors: {
            ...this._payload.chartsColors,
            [enunciadoAlterado]: newColor,
          },
        };

        updateCorrelationColorConfigData(
          this._surveyId,
          this._analyzeId,
          this._isAuto,
          chartConfigDataPayload,
          this._colorPickerColors,
          newColor,
          this._apiHasNoConfigs,
          this._dispatch,
          () => this._recalculateColorArray({ ...chartConfigDataPayload.chartsColors }),
        );
      },
      [
        this._payload,
        this._colorPickerColors,
        this._dispatch,
        this._analyzeId,
        this._surveyId,
        this._apiHasNoConfigs,
        this._isAuto,
        this._chartSeries,
        this._appTheme.title,
      ],
    );
  }

  /**
   * Override: passa `isAuto` para a action de fonte, que precisa decidir o chartType
   * (correlationAutoChart vs correlationManualChart) na URL de persistência.
   */
  updateFontSize(newFontSizeValue) {
    const newConfigData = {
      ...this._payload,
      fontsSizes: newFontSizeValue,
    };

    updateCorrelationFontSizeConfigData(
      this._surveyId,
      this._analyzeId,
      this._isAuto,
      newFontSizeValue,
      newConfigData,
      this._apiHasNoConfigs,
      this._dispatch,
    );
  }

  /**
   * Override: passa `isAuto` para a action de esquema de cores. A lógica de
   * recálculo do `chartsColors` a partir do tema neutro/padrão segue idêntica ao
   * `ThemeManager` base — só o canal de persistência muda.
   */
  updateColorScheme(newColorSchemeValue) {
    const newData = [...this._chartSeries];
    const colorSchemeColors = getChartThemeColors(
      this._appTheme.colors.charts,
      newColorSchemeValue,
    );

    const newPayload = {
      ...this._payload,
      colorSchemeValue: newColorSchemeValue,
      chartsColors:
        dataToColors(newData, colorSchemeColors) || this._payload.chartsColors,
    };

    updateCorrelationChartColorScheme(
      this._surveyId,
      this._analyzeId,
      this._isAuto,
      newPayload,
      newColorSchemeValue,
      this._apiHasNoConfigs,
      this._dispatch,
    );
  }
}
